import { Badge } from "../../ui/badge";

const statusConfig = {
  online: { color: 'bg-green-500', textColor: 'text-green-700', bgColor: 'bg-green-50', label: 'Online' },
  offline: { color: 'bg-gray-400', textColor: 'text-gray-700', bgColor: 'bg-gray-50', label: 'Offline' },
};

interface StatusIndicatorProps {
  lastSeen: string | null | undefined;
  label?: string;
  showDot?: boolean;
  isManualDevice?: boolean;
}

/** Helper: Format DateTime to readable text */
function formatDateTime(dateTimeStr: string): string {
  const date = new Date(dateTimeStr);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Helper: Determine online/offline or last seen */
function getLastSeenStatus(dateTimeStr?: string | null): string {

  if (dateTimeStr) {
    const isUTC = dateTimeStr.endsWith("Z");
    if (!isUTC) dateTimeStr = dateTimeStr + "Z";

    const dateTime = new Date(dateTimeStr);
    const now = new Date();
    const utcNow = new Date(
      Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        now.getUTCHours(),
        now.getUTCMinutes(),
        now.getUTCSeconds(),
        now.getUTCMilliseconds()
      )
    );

    const diffMs = utcNow.getTime() - dateTime.getTime();
    if (diffMs <= 5 * 60 * 1000) {
      return "online";
    } else {
      return formatDateTime(dateTimeStr);
    }
  } else {
    return "offline";
  }
}

export default function StatusIndicator({ lastSeen, label, showDot = false, isManualDevice = false }: StatusIndicatorProps) {
  const statusOrTime = isManualDevice ? "Configured" : getLastSeenStatus(lastSeen);

  // Check if it's online/offline or a timestamp
  const isOnline = statusOrTime === "online";
  const isOffline = statusOrTime === "offline";
  const isConfigured = statusOrTime === "Configured";

  const config = isConfigured ? statusConfig.online : isOnline
    ? statusConfig.online
    : isOffline
      ? statusConfig.offline
      : statusConfig.offline;

  const displayLabel = isConfigured ? "Configured" : isOnline
    ? label || config.label
    : isOffline
      ? label || config.label
      : `Offline`;

  const title =
    !isOnline && !isConfigured && lastSeen
      ? `Last seen: ${formatDateTime(lastSeen)}`
      : undefined;

  if (showDot) {
    return (
      <div className="flex items-center space-x-2" data-testid={`status-${config.label.toLowerCase()}`} title={title}>
        <div className={`w-2 h-2 rounded-full ${config.color}`} />
        <span className="text-xs text-muted-foreground">{displayLabel}</span>
      </div>
    );
  }

  return (
    <Badge
      variant="secondary"
      className={`${config.bgColor} ${config.textColor} border-0 text-xs font-medium`}
      data-testid={`badge-${config.label.toLowerCase()}`}
    >
      {displayLabel}
    </Badge>
  );
}
